import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Link2, Receipt, FileUp } from "lucide-react";
import { fetchAdmin } from "../../api";

const AdminView = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchAdmin().then(setData).catch(() => setError(true));
  }, []);

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-400" data-testid="view-admin">
        Failed to load admin console data.
      </div>
    );
  if (!data)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="view-admin">
        Loading audit evidence…
      </div>
    );

  const { audit, rules, firm_rule_count, controls, invoice, engagement, uploads } = data;

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="view-admin">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="border-b border-white/10 pb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Admin Console &amp; Audit Evidence</h1>
            <p className="text-zinc-400 text-xs mt-1">
              Engagement {engagement.id} · {engagement.state} · preparer {engagement.preparer} · reviewer {engagement.reviewer} · partner {engagement.partner}
            </p>
          </div>
          <span
            data-testid="audit-chain-badge"
            className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center ${
              audit.chain_verified
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-rose-500/10 border-rose-500/30 text-rose-400"
            }`}
          >
            <Link2 className="w-3.5 h-3.5 mr-1.5" />
            Hash Chain {audit.chain_verified ? "Verified" : "BROKEN"} · {audit.events} events
          </span>
        </div>

        {/* CONTROLS */}
        <div className="glass-card p-6 rounded-md border border-white/10 space-y-4">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <h3 className="text-sm font-bold text-white flex items-center">
              <ShieldCheck className="w-4 h-4 mr-2 text-blue-400" /> Integrity Control Executions
            </h3>
            <span className="text-xs text-zinc-400 font-mono">{controls.length} controls</span>
          </div>
          <table className="w-full text-left text-[11px]" data-testid="controls-table">
            <thead className="text-zinc-400 uppercase tracking-wider border-b border-white/10">
              <tr>
                <th className="py-2">Control</th>
                <th className="py-2">Severity</th>
                <th className="py-2 text-center">Population</th>
                <th className="py-2 text-center">Exceptions</th>
                <th className="py-2 text-center">Status</th>
                <th className="py-2">Waived By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-zinc-300">
              {controls.map((c) => (
                <tr key={c.control_id}>
                  <td className="py-2 font-mono font-bold">{c.control_id}</td>
                  <td className="py-2 text-zinc-400">{c.severity}</td>
                  <td className="py-2 text-center font-mono">{c.population}</td>
                  <td className="py-2 text-center font-mono">{c.exceptions}</td>
                  <td className="py-2 text-center">
                    {c.passed ? (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">PASS</span>
                    ) : c.open_blocking ? (
                      <span className="px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 text-[10px] font-bold">BLOCKING</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-bold">WAIVED</span>
                    )}
                  </td>
                  <td className="py-2 text-zinc-400 font-mono">{c.waived_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ENTITY RULES */}
          <div className="glass-card p-6 rounded-md border border-white/10 space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-bold text-white">Entity Rules (Deterministic)</h3>
              <span className="text-xs text-blue-400 font-mono" data-testid="rule-counts">
                {rules.length} entity · {firm_rule_count} firm
              </span>
            </div>
            <div className="space-y-2 text-xs" data-testid="entity-rules-list">
              {rules.map((rule) => (
                <div
                  key={rule.rule_id}
                  className="bg-zinc-900 p-3 rounded-md border border-white/10 flex items-center justify-between transition hover:border-white/20 hover:shadow-sm"
                >
                  <div>
                    <span className="font-bold text-white">{rule.title}</span>
                    <p className="text-zinc-400 text-[11px]">{rule.description}</p>
                  </div>
                  <span className="px-2 py-1 rounded text-[10px] font-mono bg-blue-500/10 text-blue-300">Active</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {/* BILLING */}
            <div className="glass-card p-6 rounded-md border border-white/10 space-y-3">
              <h3 className="text-sm font-bold text-white flex items-center border-b border-white/10 pb-3">
                <Receipt className="w-4 h-4 mr-2 text-blue-400" /> Line-Based Metering
              </h3>
              <div className="space-y-1.5 text-[11px]" data-testid="billing-summary">
                <div className="flex justify-between"><span className="text-zinc-400">Lines presented</span><span className="font-bold font-mono">{invoice.lines_presented}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Lines counted (billable)</span><span className="font-bold font-mono">{invoice.lines_counted}</span></div>
                {Object.entries(invoice.lines_excluded || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-zinc-400">Excluded — {k.replace("excluded_", "").replace(/_/g, " ")}</span><span className="font-bold font-mono text-purple-400">{v}</span></div>
                ))}
                <div className="flex justify-between pt-2 border-t border-white/10"><span className="text-zinc-300 font-semibold">Invoice total</span><span className="font-black font-mono text-white">{invoice.total}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Effective rate / line</span><span className="font-bold font-mono">{invoice.effective_rate_per_line}</span></div>
              </div>
            </div>

            {/* UPLOADS */}
            <div className="glass-card p-6 rounded-md border border-white/10 space-y-3">
              <h3 className="text-sm font-bold text-white flex items-center border-b border-white/10 pb-3">
                <FileUp className="w-4 h-4 mr-2 text-blue-400" /> Ingested Statements
              </h3>
              <div className="space-y-1.5 text-[11px]" data-testid="uploads-list">
                {uploads.map((u) => (
                  <div key={u.statement_id} className="flex justify-between">
                    <span className="text-zinc-400 truncate pr-2">{u.source}</span>
                    <span className="font-mono text-zinc-400 whitespace-nowrap">{u.lines} lines · {u.period}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* AUDIT ACTIONS */}
        <div className="glass-card p-6 rounded-md border border-white/10 space-y-4">
          <h3 className="text-sm font-bold text-white flex items-center border-b border-white/10 pb-3">
            <ShieldAlert className="w-4 h-4 mr-2 text-blue-400" /> Audit Log — Action Inventory
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]" data-testid="audit-actions">
            {Object.entries(audit.actions).map(([action, n]) => (
              <div key={action} className="bg-zinc-900 border border-white/10 rounded-md p-3">
                <div className="font-mono text-zinc-400 truncate">{action}</div>
                <div className="text-xl font-black text-white">{n}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminView;
